/*
  Custom cart drawer controller.

  Pairs with snippets/cart-drawer-custom.liquid (markup) and
  assets/cart-drawer-custom.css (Figma frame 943:2 styling).

  Design notes worth knowing before editing:

  * Every cart write goes through `enqueue()`, which serialises them. Shopify's
    cart is last-write-wins, so overlapping /cart/change calls silently drop
    quantity changes. Reads (recommendations) are NOT queued.

  * After each write the whole `cart-drawer` section is re-rendered from the
    Section Rendering API, so nothing inside #CartDrawer may hold state the JS
    cannot rebuild. Recommendations are therefore cached on the element and
    re-painted after every render.

  * The header cart count is updated by hand rather than by re-rendering the
    `cart-icon-bubble` section: on this theme `#cart-icon-bubble` is a
    visually-hidden anchor and assets/app.bundle.js has a MutationObserver on
    it that throws when the cart is empty (it assumes `.cart-count-bubble`
    always exists). Writing the attribute and the link labels directly avoids
    tripping that observer at all.

  * `window.fetch` / `XMLHttpRequest` are wrapped so that add-to-cart calls made
    by anything else on the page (the GemPages PDP form posts to bare
    `/cart/add`) still refresh and open this drawer. Our own requests carry an
    `X-Cart-Drawer` header so they are not mistaken for external ones.
*/

const CART_DRAWER_REQUEST_HEADER = 'X-Cart-Drawer';
const CART_WRITE_PATTERN = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
const CART_ADD_PATTERN = /\/cart\/add(\.js)?(\?|$)/;

class CustomCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.activeElement = null;
    this.writeQueue = Promise.resolve();
    this.recommendations = { signature: null, products: [], pending: null, retried: null };

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onExternalCartWrite = this.onExternalCartWrite.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    this.addEventListener('submit', this.onSubmit);
    document.addEventListener('click', this.onDocumentClick, true);
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('cart-drawer:external-write', this.onExternalCartWrite);

    installCartWriteInterceptors();
    this.syncRecommendations();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick, true);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('cart-drawer:external-write', this.onExternalCartWrite);
  }

  /* ------------------------------------------------------------ opening */

  get panel() {
    return this.querySelector('#CartDrawer');
  }

  get isOpen() {
    return this.classList.contains('is-active');
  }

  onDocumentClick(event) {
    const trigger = event.target.closest(
      '#csOpenDrawer, #cart-icon-bubble, .openCart, [data-cart-drawer-open], a[href$="/cart"], a[href*="/cart?"]'
    );
    if (!trigger || this.contains(trigger)) return;
    // A modifier click on a real /cart link should still open the cart page.
    if (trigger.tagName === 'A' && (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0)) return;

    event.preventDefault();
    event.stopPropagation();
    this.open(trigger);
  }

  onKeydown(event) {
    if (!this.isOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key === 'Tab') this.trapFocus(event);
  }

  trapFocus(event) {
    const focusable = this.panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy;
    if (this.isOpen) return;

    this.classList.add('is-active');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    this.syncRecommendations();
    window.setTimeout(() => this.panel?.focus(), 50);
  }

  close() {
    if (!this.isOpen) return;
    this.classList.remove('is-active');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    if (this.activeElement && document.contains(this.activeElement)) this.activeElement.focus();
  }

  /* ------------------------------------------------------------ actions */

  onClick(event) {
    const target = event.target.closest('[data-cart-action]');
    if (!target) return;

    const action = target.dataset.cartAction;
    if (action === 'quantity') return;

    event.preventDefault();

    if (action === 'close') return this.close();
    if (action === 'upgrade-frame') return this.upgradeFrame(target);
    if (action === 'add-recommendation') return this.addRecommendation(target);
    if (action === 'rec-increase' || action === 'rec-decrease') return this.stepRecommendation(target, action);

    const line = target.closest('[data-line-key]');
    if (!line) return;
    const input = line.querySelector('[data-cart-action="quantity"]');
    const step = Number(input.step) || 1;
    const current = Number(input.value) || 0;

    if (action === 'increase') return this.changeLine(line, current + step);
    if (action === 'decrease') return this.changeLine(line, Math.max(0, current - step));
    if (action === 'remove') return this.changeLine(line, 0);
  }

  onChange(event) {
    const target = event.target;

    if (target.dataset.cartAction === 'quantity') {
      const line = target.closest('[data-line-key]');
      if (!line) return;
      const value = Math.max(0, Number(target.value) || 0);
      return this.changeLine(line, value);
    }
  }

  onSubmit(event) {
    // Never let checkout leave with a write still in flight — the totals on the
    // button would not be the totals Shopify is about to charge.
    if (!this.classList.contains('is-busy')) return;
    event.preventDefault();
    this.announce('Your cart is still updating. Please try again in a moment.');
  }

  changeLine(line, quantity) {
    const key = line.dataset.lineKey;
    // Optimistic: reflect the intent immediately so the stepper never looks stuck.
    const input = line.querySelector('[data-cart-action="quantity"]');
    if (input) input.value = quantity;

    return this.enqueue(async () => {
      const cart = await this.cartRequest(window.routes.cart_change_url, { id: key, quantity });
      this.renderCart(cart);
    });
  }

  async upgradeFrame(button) {
    const line = button.closest('[data-line-key]');
    const key = line.dataset.lineKey;
    const variantId = Number(button.dataset.variantId);
    const quantity = Number(button.dataset.quantity) || 1;
    if (!variantId) return;

    return this.enqueue(async () => {
      // Read the live line so custom properties and any selling plan survive
      // the swap — the rendered markup only shows a filtered subset.
      const live = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null);
      const existing = live?.items?.find((item) => item.key === key);

      const payload = { id: variantId, quantity };
      if (existing?.properties && Object.keys(existing.properties).length) payload.properties = existing.properties;
      if (existing?.selling_plan_allocation?.selling_plan?.id) {
        payload.selling_plan = existing.selling_plan_allocation.selling_plan.id;
      }

      // Add the framed variant first. If the follow-up removal fails the
      // customer sees both lines and can fix it, which beats losing the item.
      await this.cartRequest(window.routes.cart_add_url, { items: [payload] });
      const cart = await this.cartRequest(window.routes.cart_change_url, { id: key, quantity: 0 });
      this.renderCart(cart);
      this.announce('Frame added.');
    });
  }

  /* ---------------------------------------------------- recommendations */

  get recommendationConfig() {
    return {
      enabled: this.dataset.recommendationsEnabled !== 'false',
      limit: Math.max(1, Number(this.dataset.recommendationsLimit) || 1),
      key: this.dataset.rebuyKey || window.Rebuy?.shop?.api_key || '',
      dataSourceId: (this.dataset.rebuyDataSource || '').trim(),
      cartProductIds: (this.dataset.cartProductIds || '').split(',').filter(Boolean),
    };
  }

  syncRecommendations() {
    const slot = this.querySelector('[data-cart-recommendations]');
    if (!slot) return;

    const config = this.recommendationConfig;
    if (!config.enabled || !config.cartProductIds.length) return;

    const signature = config.cartProductIds.join(',');
    if (this.recommendations.signature === signature) {
      // Cart contents unchanged — repaint from cache so a re-render does not
      // flash an empty slot or re-hit Rebuy.
      this.paintRecommendations(this.recommendations.products);
      return;
    }
    if (this.recommendations.pending === signature) return;

    this.recommendations.pending = signature;
    this.fetchRecommendations(config)
      .then((products) => {
        if (this.recommendations.pending !== signature) return;
        this.recommendations = { signature, products, pending: null, retried: this.recommendations.retried };
        this.paintRecommendations(products);
      })
      .catch((error) => {
        // Leave the signature unset so the next render or open retries rather
        // than caching a transient failure as "no recommendations". A drawer
        // that is already open would otherwise sit empty until the shopper
        // closed and reopened it, so also retry once on a short delay.
        console.warn('Cart drawer: recommendations unavailable', error);
        this.recommendations.pending = null;
        if (this.recommendations.retried === signature) return;
        this.recommendations.retried = signature;
        window.setTimeout(() => this.syncRecommendations(), 2000);
      });
  }

  async fetchRecommendations(config) {
    // Rebuy's own script publishes the shop's public key on window.Rebuy, and
    // it loads independently of this drawer — so when no key is configured in
    // theme settings, wait for that script rather than giving up on the first
    // (usually too early) attempt and caching an empty result forever.
    const key = config.key || (await waitForRebuyKey());
    // Throw rather than return [] — an empty array is cached as a definitive
    // "this cart has no recommendations", and a key that has not arrived yet is
    // a transient failure that must stay retryable.
    if (!key) throw new Error('Rebuy public key unavailable');

    // A configured Rebuy Data Source wins; otherwise use the shop-level
    // recommendation engine. Both honour what is set up in the Rebuy admin.
    const endpoint = config.dataSourceId
      ? `https://rebuyengine.com/api/v1/custom/id/${encodeURIComponent(config.dataSourceId)}`
      : 'https://rebuyengine.com/api/v1/products/recommended';

    const params = new URLSearchParams({
      key,
      shopify_product_ids: config.cartProductIds.join(','),
      // Over-fetch: products already in the cart are filtered out below.
      limit: String(config.limit + config.cartProductIds.length + 2),
    });

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 4000);
    try {
      const response = await fetch(`${endpoint}?${params}`, { signal: controller.signal });
      if (!response.ok) throw new Error(`Rebuy responded ${response.status}`);
      const body = await response.json();
      const inCart = new Set(config.cartProductIds.map(String));
      return (body.data || [])
        .filter((product) => product && !inCart.has(String(product.id)))
        .filter((product) => (product.variants || []).some((variant) => variantIsAvailable(variant)))
        .slice(0, config.limit);
    } finally {
      window.clearTimeout(timeout);
    }
  }

  paintRecommendations(products) {
    const slot = this.querySelector('[data-cart-recommendations]');
    const list = this.querySelector('[data-cart-recommendations-list]');
    if (!slot || !list) return;

    if (!products || !products.length) {
      slot.hidden = true;
      list.innerHTML = '';
      return;
    }

    // Recommendations pair with the first line in the cart: its title, its
    // size, its frame style and its discount rate.
    const firstLine = this.querySelector('.cart-drawer-custom__item');
    const pairing = {
      title: firstLine?.querySelector('.cart-drawer-custom__item-title')?.textContent.trim() || '',
      size: firstLine?.dataset.optionSize || '',
      sizePosition: Number(firstLine?.dataset.sizePosition ?? -1),
      style: firstLine?.dataset.optionStyle || '',
      ratio: Number(firstLine?.dataset.discountRatio) || 1,
    };
    list.innerHTML = products.map((product) => this.recommendationCard(product, pairing)).join('');
    slot.hidden = false;
  }

  recommendationCard(product, pairing) {
    const variants = (product.variants || []).filter(variantIsAvailable);
    if (!variants.length) return '';

    const variant = pairedVariant(product, variants, pairing);
    const image = product.image?.src ? `${product.image.src}${product.image.src.includes('?') ? '&' : '?'}width=270` : '';
    const link = escapeHtml(product.link || `/products/${product.handle}`);
    const variantLine = this.variantDescription(product, variant);
    const descriptor = [product.product_type, pairing.title ? `pairs with ${pairing.title}` : '']
      .filter(Boolean)
      .join(' · ');

    return `
      <article class="cart-drawer-custom__rec" data-rec-card data-product-id="${product.id}" data-variant-id="${variant.id}">
        <a class="cart-drawer-custom__thumb" href="${link}" tabindex="-1" aria-hidden="true">
          ${image ? `<img src="${escapeHtml(image)}" alt="" width="90" height="112" loading="lazy">` : ''}
        </a>
        <div class="cart-drawer-custom__item-body">
          <div class="cart-drawer-custom__item-head">
            <div class="cart-drawer-custom__item-text">
              <a class="cart-drawer-custom__item-title" href="${link}">${escapeHtml(product.title)}</a>
              ${variantLine ? `<p class="cart-drawer-custom__item-subtitle">${variantLine}</p>` : ''}
              ${descriptor ? `<p class="cart-drawer-custom__item-subtitle">${escapeHtml(descriptor)}</p>` : ''}
            </div>
            <div class="cart-drawer-custom__item-price">${recommendationPrice(variant, pairing.ratio)}</div>
          </div>

          <div class="cart-drawer-custom__item-actions">
            <div>
              <div class="cart-drawer-custom__qty">
                <button type="button" data-cart-action="rec-decrease" aria-label="Decrease quantity">&ndash;</button>
                <input type="number" value="1" min="1" inputmode="numeric" data-rec-quantity aria-label="Quantity">
                <button type="button" data-cart-action="rec-increase" aria-label="Increase quantity">+</button>
              </div>
              <button type="button" class="cart-drawer-custom__rec-add" data-cart-action="add-recommendation">Add</button>
            </div>
          </div>
        </div>
      </article>`;
  }

  /* Same wording as a cart line: 16 × 8" · Print only / Black frame. */
  variantDescription(product, variant) {
    const parts = [];
    (product.options || []).forEach((option, index) => {
      const name = String(option.name || '').toLowerCase();
      const value = variant[`option${index + 1}`];
      if (!value) return;
      if (name === 'size') parts.push(`${escapeHtml(value).replace('x', ' &times; ')}"`);
      if (name === 'style') {
        parts.push(
          value === this.dataset.framePrintValue
            ? escapeHtml(this.dataset.printOnlyLabel)
            : `${escapeHtml(value)} ${escapeHtml(this.dataset.frameSuffixLabel)}`
        );
      }
    });
    return parts.join(' &middot; ');
  }

  stepRecommendation(button, action) {
    const input = button.closest('[data-rec-card]')?.querySelector('[data-rec-quantity]');
    if (!input) return;
    const next = (Number(input.value) || 1) + (action === 'rec-increase' ? 1 : -1);
    input.value = Math.max(1, next);
  }

  addRecommendation(button) {
    const card = button.closest('[data-rec-card]');
    if (!card) return;

    const variantId = Number(card.dataset.variantId);
    const quantity = Math.max(1, Number(card.querySelector('[data-rec-quantity]')?.value) || 1);
    if (!variantId) return;

    return this.enqueue(async () => {
      // /cart/add returns the added lines, not the cart, so the totals and
      // item_count have to come from a follow-up read.
      await this.cartRequest(window.routes.cart_add_url, { items: [{ id: variantId, quantity }] });
      this.renderCart(await this.fetchCartWithSections());
      this.announce('Added to your cart.');
    });
  }

  /* ----------------------------------------------------------- plumbing */

  enqueue(operation) {
    this.writeQueue = this.writeQueue.then(async () => {
      this.setBusy(true);
      this.showError('');
      try {
        await operation();
      } catch (error) {
        console.error('Cart drawer: update failed', error);
        this.showError(error?.message || 'We could not update your cart. Please try again.');
        try {
          this.renderCart(await this.fetchCartWithSections());
        } catch (_) {
          /* leave the current markup in place rather than blanking the drawer */
        }
      } finally {
        this.setBusy(false);
      }
    });
    return this.writeQueue;
  }

  async cartRequest(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        [CART_DRAWER_REQUEST_HEADER]: '1',
      },
      body: JSON.stringify({
        ...body,
        sections: this.sectionsToRender,
        sections_url: window.location.pathname,
      }),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status || data.errors) {
      throw new Error(data.description || data.message || 'Cart request failed');
    }
    return data;
  }

  get sectionsToRender() {
    return ['cart-drawer'];
  }

  /*
    Dawn compatibility surface.

    assets/product-form.js (and quick-add) look up the page's cart element and
    call getSectionsToRender()/setActiveElement()/renderContents() on it. Those
    are the theme's add-to-cart path for every PDP, so this element has to
    answer them in Dawn's shape or add-to-cart throws and nothing reaches the
    cart. Do not rename these.
  */
  getSectionsToRender() {
    return this.sectionsToRender.map((id) => ({ id, selector: '#CartDrawer' }));
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  renderContents(response) {
    // /cart/add responses carry `sections` but no cart totals, so the counts
    // are read back out of the freshly rendered markup in renderCart().
    this.renderCart(response);
    this.open();
  }

  /*
    Re-read the cart *and* its rendered section in one shot.

    Deliberately a POST to /cart/update with an empty `updates` map rather than
    `GET /cart?sections=…`: the GET is subject to the browser's HTTP cache and
    was observed serving pre-write markup straight after a successful add, so
    the drawer silently re-rendered the old line items. The POST cannot be
    cached and, unlike /cart/add, returns the whole cart object (item_count and
    totals) alongside the sections.
  */
  async fetchCartWithSections() {
    return this.cartRequest(window.routes.cart_update_url, { updates: {} });
  }

  renderCart(cart) {
    const html = cart?.sections?.['cart-drawer'];
    const panel = this.panel;
    if (!html || !panel) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const nextPanel = parsed.querySelector('#CartDrawer');
    const nextRoot = parsed.querySelector('cart-drawer');
    if (!nextPanel) return;

    const scrollTop = panel.querySelector('[data-cart-scroll]')?.scrollTop || 0;
    panel.innerHTML = nextPanel.innerHTML;

    if (nextRoot) {
      Object.keys(nextRoot.dataset).forEach((name) => {
        this.dataset[name] = nextRoot.dataset[name];
      });
    }

    const scroller = panel.querySelector('[data-cart-scroll]');
    if (scroller) scroller.scrollTop = scrollTop;

    // Prefer the count rendered into the markup we just painted: /cart/add
    // responses have no item_count, and the attribute is always in step with
    // what the shopper is actually looking at.
    const itemCount = Number(this.dataset.cartCount ?? cart.item_count ?? 0);
    this.classList.toggle('is-empty', itemCount === 0);
    this.updateHeaderCount(itemCount);
    this.syncRecommendations();

    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-custom', cartData: cart });
    }
    document.dispatchEvent(new CustomEvent('cart-drawer:updated', { detail: { cart } }));
  }

  updateHeaderCount(count) {
    const total = String(count ?? 0);
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble) bubble.setAttribute('carttotal', total);

    document.querySelectorAll('.openCart').forEach((link) => {
      link.querySelectorAll('span.linkText, span.linkTextMobile').forEach((label) => {
        label.innerHTML = `Cart [${total}]`;
      });
    });
  }

  onExternalCartWrite(event) {
    const wasAdd = Boolean(event?.detail?.added);
    this.enqueue(async () => {
      this.renderCart(await this.fetchCartWithSections());
      // Surfacing the drawer is right after an add-to-cart, but wrong when the
      // shopper is editing quantities on the /cart page itself.
      if (wasAdd && !window.location.pathname.startsWith(window.routes.cart_url)) this.open();
    });
  }

  setBusy(isBusy) {
    this.classList.toggle('is-busy', isBusy);
    this.setAttribute('aria-busy', isBusy ? 'true' : 'false');
  }

  showError(message) {
    const node = this.querySelector('[data-cart-error]');
    if (!node) return;
    node.textContent = message || '';
    node.hidden = !message;
  }

  announce(message) {
    const node = this.querySelector('[data-cart-status]');
    if (node) node.textContent = message;
  }
}

/* -------------------------------------------------------------- helpers */

/*
  Resolves with Rebuy's public storefront key once its script has booted, or
  with '' after ~8s so a shop without Rebuy installed simply shows no
  recommendations instead of hanging.
*/
let rebuyKeyPromise = null;

function waitForRebuyKey() {
  if (window.Rebuy?.shop?.api_key) return Promise.resolve(window.Rebuy.shop.api_key);
  if (rebuyKeyPromise) return rebuyKeyPromise;

  rebuyKeyPromise = new Promise((resolve) => {
    const deadline = Date.now() + 8000;
    const poll = window.setInterval(() => {
      const key = window.Rebuy?.shop?.api_key;
      if (key || Date.now() > deadline) {
        window.clearInterval(poll);
        resolve(key || '');
      }
    }, 150);
  });
  return rebuyKeyPromise;
}

/*
  Picks the variant of a recommended product that corresponds to the paired
  cart line. Artworks come in different aspect ratios (Stick + Sand is 8x12,
  16x24, ...; Root + Sky is 16x8, 24x12, ...), so the size is matched by its
  position in the size list when the exact value does not exist: the smallest
  print pairs with the smallest print. The frame style is matched by value.
  Falls back to the cheapest available variant.
*/
function pairedVariant(product, variants, pairing) {
  const cheapest = (list) =>
    list.reduce((low, variant) => (toCents(variant.price) < toCents(low.price) ? variant : low));
  const optionIndex = (name) =>
    (product.options || []).findIndex((option) => String(option.name || '').toLowerCase() === name);

  const sizeIndex = optionIndex('size');
  if (sizeIndex < 0 || (!pairing.size && pairing.sizePosition < 0)) return cheapest(variants);

  const sizes = product.options[sizeIndex].values || [];
  let size = sizes.includes(pairing.size) ? pairing.size : null;
  if (!size && pairing.sizePosition >= 0 && sizes.length) size = sizes[Math.min(pairing.sizePosition, sizes.length - 1)];

  const sameSize = variants.filter((variant) => variant[`option${sizeIndex + 1}`] === size);
  if (!sameSize.length) return cheapest(variants);

  const styleIndex = optionIndex('style');
  const sameStyle =
    styleIndex >= 0 && pairing.style
      ? sameSize.find((variant) => variant[`option${styleIndex + 1}`] === pairing.style)
      : null;
  return sameStyle || cheapest(sameSize);
}

/*
  Priced like a cart line: original struck through, then the current price.
  The current price applies the paired cart line's own percentage discount
  rate (1 when that line has none, or has a fixed-amount discount). A variant
  compare_at_price, when higher, is used as the original.
*/
function recommendationPrice(variant, ratio) {
  const price = toCents(variant.price);
  const compareAt = variant.compare_at_price == null ? 0 : toCents(variant.compare_at_price);
  const original = Math.max(price, compareAt);
  const current = Math.round(price * (ratio > 0 && ratio <= 1 ? ratio : 1));
  if (current < original) return `<s>${formatMoney(original)}</s>${formatMoney(current)}`;
  return formatMoney(current);
}

function variantIsAvailable(variant) {
  if (!variant) return false;
  if (variant.available === false) return false;
  if (variant.inventory_management !== 'shopify') return true;
  if (variant.inventory_policy === 'continue') return true;
  return (variant.inventory_quantity ?? 0) > 0;
}

function toCents(price) {
  if (typeof price === 'number') return price > 1000 ? price : Math.round(price * 100);
  return Math.round(parseFloat(price || '0') * 100);
}

/*
  Mirrors Liquid's `money_without_trailing_zeros` against the shop's money
  format, which is what the Figma frame shows ("$45", not "$45.00").
*/
function formatMoney(cents) {
  const format = document.querySelector('cart-drawer')?.dataset.moneyFormat || '${{amount}}';
  const value = (cents / 100).toFixed(2);
  const [whole, decimals] = value.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const amount = decimals === '00' ? grouped : `${grouped}.${decimals}`;
  return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
}

function escapeHtml(value) {
  return String(value ?? '').replace(
    /[&<>"']/g,
    (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character])
  );
}

/*
  Catch cart writes made by anything other than this drawer (the PDP add-to-cart
  posts to bare /cart/add, apps use XHR) and re-open the drawer with fresh
  contents. Installed once, guarded so a re-registered element cannot stack
  wrappers.
*/
let cartWriteInterceptorsInstalled = false;

function installCartWriteInterceptors() {
  if (cartWriteInterceptorsInstalled) return;
  cartWriteInterceptorsInstalled = true;

  const notify = debounce((added) => {
    document.dispatchEvent(new CustomEvent('cart-drawer:external-write', { detail: { added } }));
  }, 120);

  const nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : input?.url || '';
      const method = (init?.method || (typeof input === 'object' && input?.method) || 'GET').toUpperCase();
      const isWrite = method === 'POST' && CART_WRITE_PATTERN.test(url);
      const isOurs = hasDrawerHeader(init?.headers) || hasDrawerHeader(typeof input === 'object' ? input?.headers : null);

      return nativeFetch.apply(this, arguments).then((response) => {
        if (isWrite && !isOurs && response.ok) notify(CART_ADD_PATTERN.test(url));
        return response;
      });
    };
  }

  const nativeOpen = XMLHttpRequest.prototype.open;
  const nativeSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this.__cartDrawerWrite = String(method).toUpperCase() === 'POST' && CART_WRITE_PATTERN.test(String(url));
    this.__cartDrawerAdd = CART_ADD_PATTERN.test(String(url));
    return nativeOpen.call(this, method, url, ...rest);
  };
  XMLHttpRequest.prototype.send = function (...args) {
    if (this.__cartDrawerWrite) {
      this.addEventListener('load', () => {
        if (this.status >= 200 && this.status < 300) notify(this.__cartDrawerAdd);
      });
    }
    return nativeSend.apply(this, args);
  };
}

function hasDrawerHeader(headers) {
  if (!headers) return false;
  if (typeof headers.get === 'function') return Boolean(headers.get(CART_DRAWER_REQUEST_HEADER));
  return Object.keys(headers).some((name) => name.toLowerCase() === CART_DRAWER_REQUEST_HEADER.toLowerCase());
}

function debounce(callback, wait) {
  let timer;
  return (...args) => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => callback(...args), wait);
  };
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CustomCartDrawer);
}
